import React, { useState } from 'react';
import { MapPin, Mail, Phone, Clock, Send, Instagram, Youtube, Facebook, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { contactAPI, newsletterAPI } from '../services/api';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    type: 'general'
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setFormLoading(true);
      await contactAPI.send(formData);
      setFormMessage('Message envoyé avec succès !');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        type: 'general'
      });
    } catch (error) {
      setFormMessage('Erreur lors de l\'envoi du message');
    } finally {
      setFormLoading(false);
      setTimeout(() => setFormMessage(''), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    try {
      setNewsletterLoading(true);
      await newsletterAPI.subscribe(newsletterEmail);
      setNewsletterMessage('Inscription réussie !');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterMessage('Erreur lors de l\'inscription');
    } finally {
      setNewsletterLoading(false);
      setTimeout(() => setNewsletterMessage(''), 3000);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.info.address'),
      details: ['Almadies', 'Dakar, Sénégal'],
      color: 'text-blue-600'
    },
    {
      icon: Mail,
      title: t('contact.info.email'),
      details: ['contact@ascsurf.com'],
      color: 'text-teal-600'
    },
    {
      icon: Phone,
      title: t('contact.info.phone'),
      details: ['+221 77 197 67 67'],
      color: 'text-orange-600'
    }
  ];

  const socialMedia = [
    {
      name: t('contact.social.instagram'),
      handle: '@asc_surf_confederation_',
      icon: Instagram,
      color: 'bg-pink-500 hover:bg-pink-600',
      followers: '25K'
    },
    {
      name: t('contact.social.youtube'),
      handle: 'ASCsurf Official',
      icon: Youtube,
      color: 'bg-red-500 hover:bg-red-600',
      followers: '12K'
    },
    {
      name: t('contact.social.facebook'),
      handle: 'ASCsurf',
      icon: Facebook,
      color: 'bg-blue-600 hover:bg-blue-700',
      followers: '18K'
    }
  ];

  const departments = [
    {
      name: t('contact.departments.management'),
      contact: 'Oumar Seye',
      email: 'president@ascsurf.com',
      phone: '+221 77 123 45 67'
    },
    {
      name: t('contact.departments.one'),
      contact: t('contact.departments.one'),
      email: 'marketing@ascsurf.com',
      phone: '+221 77 123 45 67'
    },
    {
      name: t('contact.departments.two'),
      contact: t('contact.departments.two'),
      email: 'com-technique@ascsurf.com',
      phone: '+221 77 345 67 89'
    },
    {
      name: 'Contact',
      contact: 'Contact',
      email: 'contact@ascsurf.com',
      phone: '+221 77 456 78 90'
    }
  ];

  return (
    <div className="">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-teal-800 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://i.imgur.com/mrt24S8.jpeg?auto=compress&cs=tinysrgb&w=1600" 
            alt="Contact ASCsurf"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            {t('contact.hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('contact.form.title')}
              </h2>
              
              {formMessage && (
                <div className={`mb-6 p-4 rounded-lg ${
                  formMessage.includes('succès') 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {formMessage}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.name')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder={t('contact.form.placeholder.name')}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder={t('contact.form.placeholder.email')}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.type')}
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  >
                    <option value="general">{t('contact.form.types.general')}</option>
                    <option value="partnership">{t('contact.form.types.partnership')}</option>
                    <option value="competition">{t('contact.form.types.competition')}</option>
                    <option value="media">{t('contact.form.types.media')}</option>
                    <option value="athlete">{t('contact.form.types.athlete')}</option>
                    <option value="federation">{t('contact.form.types.federation')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.subject')} *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder={t('contact.form.placeholder.subject')}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.message')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                    placeholder={t('contact.form.placeholder.message')}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={formLoading}
                  className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="h-5 w-5" />
                  <span>{formLoading ? 'Envoi...' : t('contact.form.send')}</span>
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  {t('contact.info.title')}
                </h2>
                
                <div className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className={`${info.color} bg-gray-50 p-3 rounded-lg`}>
                        <info.icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{info.title}</h3>
                        {info.details.map((detail, idx) => (
                          <p key={idx} className="text-gray-600">{detail}</p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('news.social.title')}
                </h3>
                
                <div className="space-y-4">
                  {socialMedia.map((social, index) => (
                    <a
                      key={index}
                      href="#"
                      className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-300 group"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`${social.color} p-3 rounded-lg transition-colors`}>
                          <social.icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{social.name}</h4>
                          <p className="text-gray-600 text-sm">{social.handle}</p>
                        </div>
                      </div>
                      
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Departments */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              {t('contact.departments.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('contact.departments.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {departments.map((dept, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-blue-50 p-6 rounded-2xl border border-blue-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{dept.name}</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700 font-medium">{dept.contact}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="h-4 w-4 text-teal-600" />
                    <a href={`mailto:${dept.email}`} className="text-teal-600 hover:text-teal-800 transition-colors">
                      {dept.email}
                    </a>
                  </div>
        
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              {t('contact.location.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('contact.location.subtitle')}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Map Placeholder */}
            <div className="h-96 lg:h-auto relative">
  <iframe
    className="absolute inset-0 w-full h-full"
    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3858.5133076288985!2d-17.52131312389438!3d14.740086385763064!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xec112db91cc7f59%3A0xda047a2edc522cf!2sSurfer%20Paradise!5e0!3m2!1sfr!2ssn!4v1717589629760!5m2!1sfr!2ssn"
    loading="lazy"
    referrerPolicy="no-referrer-when-downgrade"
  ></iframe>
</div>
              
              {/* Location Details */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('contact.location.find')}
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">{t('contact.location.address')}</h4>
                    <p className="text-gray-600">
                      Corniche des almadies<br />
                      Dakar, Sénégal
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">{t('contact.location.transport')}</h4>
                    <ul className="text-gray-600 space-y-1">
                      <li>•{t('contact.location.taxi')}</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">{t('contact.location.landmarks')}</h4>
                    <ul className="text-gray-600 space-y-1">
                      <li>• {t('contact.location.repere')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('contact.newsletter.title')}
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            {t('contact.newsletter.subtitle')}
          </p>
          
          {newsletterMessage && (
            <div className="mb-6 p-4 bg-white/20 rounded-lg text-white">
              {newsletterMessage}
            </div>
          )}
          
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder={t('news.newsletter.placeholder')}
              className="flex-1 px-6 py-4 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-white/30"
              required
            />
            <button 
              type="submit"
              disabled={newsletterLoading}
              className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              <Globe className="h-5 w-5" />
              <span>{newsletterLoading ? 'Inscription...' : t('news.newsletter.subscribe')}</span>
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;